/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React, { RefObject } from 'react'
import { View, Text, Icon, Radiobutton, Pressable } from '@components/atoms'
import { mono, boFont } from '@components/systems/backoffice/backoffice.tokens'

import { MenuTreeNodeType } from '@redux/index'

export type CustomDataType = {
	fileType: string
	fileSize: string
}

interface PropsType {
	node: MenuTreeNodeType
	hasChildren: boolean
	childrenNodeList: MenuTreeNodeType[]
	depth: number
	isOpen: boolean
	handleRef?: RefObject<any>
	onToggle: (id: MenuTreeNodeType['id']) => void
	showCheckBox: boolean
	showRadiobutton: boolean
	hideObject: boolean
	selectedRadio: string | number
	disableDragging: boolean
	showDragHandle: boolean
	onRadioChange: (value: string | number) => void
	onNodeClick?: (node: MenuTreeNodeType) => void
	showRootChevronButton: boolean
	selectedMenuId?: string
	nodeRightAccessory?: (node: MenuTreeNodeType) => JSX.Element
	className?: string
	style?: React.CSSProperties
}

/** 트리 한 행 — 36px 헤어라인. 선택 = ink 글자 + 옅은 잉크 면 + 좌측 1px ink */
export const DndTreeNode = (props: PropsType) => {
	const { droppable } = props.node

	// variables
	const indent = React.useMemo(() => props.depth * 24, [])
	const childrenNodeLength: number = props.childrenNodeList.length ?? 0

	const handleToggle = React.useCallback(
		(e: React.MouseEvent) => {
			e.stopPropagation()
			props.onToggle(props.node.id)
		},
		[props.onToggle, props.node.id],
	)

	const onNodeClick = React.useCallback(() => {
		if (props.onNodeClick !== undefined) {
			props.onNodeClick(props.node)
		}
	}, [props.onNodeClick, props.node.id])

	return (
		<>
			{props.hideObject && !droppable ? (
				<></>
			) : (
				<View
					css={[style, { paddingInlineStart: indent }, { ...props.style }]}
					className={`tree-node ${props.className ? props.className : ''} ${
						props.selectedMenuId === props.node.id ? 'selected' : ''
					}`}
				>
					{!props.disableDragging ? (
						<View
							className="drag-handle"
							css={!props.showDragHandle ? { width: 0 } : null}
							ref={props.handleRef}
							data-testid={`drag-handle-${props.node.id}`}
						>
							{props.showDragHandle ? (
								<Icon name="drag-horizontal-variant" className="nodeDragHandleIcon" />
							) : null}
						</View>
					) : null}

					{props.showRootChevronButton && (
						<Pressable
							className="nodeChevronButton"
							onClick={props.hasChildren ? handleToggle : () => {}}
						>
							<View
								className={`expandIconWrapper ${
									props.isOpen && childrenNodeLength > 0 ? 'isOpen' : ''
								} ${!props.hasChildren ? 'disabled' : ''}`}
							>
								{props.node.droppable ? (
									<View>
										<Icon name="chevron-right" className="expandIcon" />
									</View>
								) : null}
							</View>
						</Pressable>
					)}

					<Pressable className="nodeButton" onClick={onNodeClick}>
						<View>
							<Icon
								name={
									droppable
										? 'folder-outline'
										: props.node.icon
										? props.node.icon
										: 'application-brackets-outline'
								}
								className="nodeIcon"
							/>
						</View>

						<View className="labelGridItem">
							<Text as="span" className="nodeLabel" i18n={props.node.i18n}>
								{props.node.text}
							</Text>
						</View>

						{props.node.droppable ? (
							<span className="childrenNodeCount">{childrenNodeLength}</span>
						) : null}
					</Pressable>

					{props.showCheckBox ? (
						<View>
							<Radiobutton
								value={props.node.id}
								color={mono.ink}
								onRadioChange={props.onRadioChange}
								selectedValue={props.selectedRadio}
							/>
						</View>
					) : props.nodeRightAccessory ? (
						<>{props.nodeRightAccessory(props.node)}</>
					) : null}
				</View>
			)}
		</>
	)
}

const style = css({
	position: 'relative',
	display: 'flex',
	alignItems: 'center',
	height: 36,
	width: '100%',
	borderRadius: 0,
	borderBottom: `1px solid ${mono.line}`,
	borderLeft: '1px solid transparent',
	fontFamily: boFont.ui,
	color: mono.dim,
	transition:
		'color 200ms ease-out, background-color 200ms ease-out, border-color 200ms ease-out',

	'&:hover': { color: mono.ink, background: 'rgba(22, 24, 27, 0.03)' },

	'.drag-handle': {
		display: 'flex',
		alignItems: 'center',
		cursor: 'grab',
	},

	'.nodeDragHandleIcon': {
		fontSize: 16,
		color: mono.faint,
	},

	'.nodeChevronButton': {
		height: '100%',
		padding: 0,
	},

	'.nodeButton': {
		display: 'flex',
		justifyContent: 'flex-start',
		alignItems: 'center',
		gap: 8,
		width: '100%',
		height: '100%',
		padding: 0,
		color: 'inherit',

		'.nodeIcon': {
			fontSize: 16,
			color: 'inherit',
		},

		'.labelGridItem': {
			minWidth: 0,
			marginRight: 4,

			'.nodeLabel': {
				fontFamily: 'inherit',
				fontSize: 13,
				fontWeight: 400,
				lineHeight: 1.3,
				color: 'inherit',
				whiteSpace: 'nowrap',
				overflow: 'hidden',
				textOverflow: 'ellipsis',
			},
		},

		'.childrenNodeCount': {
			fontSize: 11,
			color: mono.faint,
			fontVariantNumeric: 'tabular-nums',
		},
	},

	'.expandIconWrapper': {
		alignItems: 'center',
		fontSize: 0,
		cursor: 'pointer',
		display: 'flex',
		height: 24,
		justifyContent: 'center',
		width: 24,
		transition: 'transform 200ms ease-out',
		transform: 'rotate(0deg)',

		'.expandIcon': { fontSize: 16, color: mono.faint },

		'&.isOpen': {
			transform: 'rotate(90deg)',
		},

		'&.disabled': {
			opacity: 0.2,
		},
	},

	'&.selected': {
		background: 'rgba(22, 24, 27, 0.05)',
		borderLeftColor: mono.ink,
		color: mono.ink,

		'.nodeLabel': { fontWeight: 500 },
	},
})
